<template>
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center" @click.self="close">
        <!-- 배경 오버레이 -->
        <div class="absolute inset-0 bg-black bg-opacity-30" @click="close"></div>

        <!-- 팝업 컨텐츠 -->
        <div class="relative bg-white rounded-lg shadow-xl w-80 z-10" @click.stop>
            <!-- 헤더 -->
            <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-gray-800">라벨 관리</h3>
                <button @click="close" class="text-gray-400 hover:text-gray-600">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- 라벨 목록 -->
            <div class="p-3 space-y-2">
                <div v-for="label in labels" :key="label.id"
                    class="flex items-center gap-3 p-2 rounded hover:bg-gray-50 group">
                    <!-- 색상 원 -->
                    <div class="w-4 h-4 rounded-full flex-shrink-0" :style="{ backgroundColor: label.colorCode }"></div>

                    <!-- 라벨 이름 (편집 가능) -->
                    <input v-if="editingLabelId === label.id" v-model="editingName" @blur="handleBlur(label.id)"
                        @keydown.enter="handleEnter(label.id, $event)" @keydown.esc="cancelEdit"
                        @compositionstart="isComposing = true" @compositionend="isComposing = false" ref="editInput"
                        type="text"
                        class="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        maxlength="20" />
                    <div v-else @click="startEdit(label)"
                        class="flex-1 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                        {{ label.name }}
                    </div>

                    <!-- 편집 아이콘 -->
                    <button v-if="editingLabelId !== label.id" @click="startEdit(label)"
                        class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                    </button>
                </div>

                <!-- 라벨이 없을 때 -->
                <div v-if="labels.length === 0" class="text-center py-6 text-sm text-gray-500">
                    라벨이 없습니다
                </div>
            </div>

            <!-- 안내 메시지 -->
            <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                <p class="text-xs text-gray-500">
                    라벨 이름을 클릭하여 수정할 수 있습니다
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, nextTick } from 'vue';
import { useQuoteLabel } from '@/composables/useQuoteLabel';

export default {
    name: 'LabelManager',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const { labels, updateLabel, fetchLabels } = useQuoteLabel();

        const isOpen = ref(props.modelValue);
        const editingLabelId = ref(null);
        const editingName = ref('');
        const editInput = ref(null);
        const isComposing = ref(false); // IME 조합 중 여부

        watch(() => props.modelValue, (newValue) => {
            isOpen.value = newValue;
            // 열릴 때 라벨 목록 새로 조회
            if (newValue) {
                fetchLabels(true);
            }
        });

        const close = () => {
            isOpen.value = false;
            emit('update:modelValue', false);
            cancelEdit();
        };

        const startEdit = (label) => {
            editingLabelId.value = label.id;
            editingName.value = label.name;
            nextTick(() => {
                if (editInput.value) {
                    editInput.value[0]?.focus();
                    editInput.value[0]?.select();
                }
            });
        };

        const finishEdit = async (labelId) => {
            if (!editingName.value.trim()) {
                cancelEdit();
                return;
            }

            const label = labels.value.find(l => l.id === labelId);
            if (label && editingName.value !== label.name) {
                try {
                    await updateLabel(labelId, editingName.value.trim(), label.colorCode);
                } catch (error) {
                    console.error('라벨 수정 실패:', error);
                }
            }

            editingLabelId.value = null;
            editingName.value = '';
        };

        // blur 이벤트 핸들러 - IME 조합 중이면 약간 지연 후 처리
        const handleBlur = (labelId) => {
            if (isComposing.value) {
                // IME 조합 중이면 조합이 완료될 때까지 잠시 대기
                setTimeout(() => {
                    finishEdit(labelId);
                }, 10);
            } else {
                finishEdit(labelId);
            }
        };

        // Enter 키 핸들러 - IME 조합 중이면 무시
        const handleEnter = (labelId, event) => {
            if (isComposing.value) {
                // IME 조합 중에는 Enter 키 무시 (조합 확정용)
                return;
            }
            event.preventDefault();
            finishEdit(labelId);
        };

        const cancelEdit = () => {
            editingLabelId.value = null;
            editingName.value = '';
        };

        return {
            isOpen,
            labels,
            editingLabelId,
            editingName,
            editInput,
            isComposing,
            close,
            startEdit,
            finishEdit,
            handleBlur,
            handleEnter,
            cancelEdit,
        };
    },
};
</script>

<style scoped>
/* 팝업 애니메이션 */
.fixed {
    animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}
</style>
